import type { FC } from 'react';
import React, { useCallback, useState, useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { useSessionData } from '@confluence/session-data';

import {
	AutoConversionByLineEvent,
	AutoConvertActionSubject,
	AutoConvertEvent,
	sendEditorConversionAnalyticsPageEvent,
} from '../analytics';
import { EditorConversionModalComponent } from '../EditorConversionNotification/EditorConversionModalComponent';
import { useAutoConvertState } from '../AutoConversion/useAutoConvertState';
import { ALL_PAGES_FF } from '../AutoConversion/constants';

import { useAutoConversionByLineContext } from './AutoConversionByLineContext';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledByLine = styled.a({
	cursor: 'pointer',
	marginRight: token('space.050', '4px'),
	paddingLeft: token('space.050', '4px'),
	color: token('color.text.subtlest', '--ds-text-subtlest'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:before': {
		content: '•',
		display: 'inline-block',
		color: token('color.text.subtlest', '--ds-text-subtlest'),
		paddingRight: token('space.100', '8px'),
		textDecoration: 'none',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:hover:before': {
		textDecoration: 'none',
	},
});

export const i18n = defineMessages({
	byLine: {
		id: 'editor-conversion.auto-conversion-byline.byline',
		defaultMessage: '{editorType}',
		description: 'Editor type',
	},
	newEditorTooltip: {
		id: 'editor-conversion.auto-conversion-byline.newEditorTooltip',
		defaultMessage: 'This page is using the new editor.',
	},
	legacyEditorTooltip: {
		id: 'editor-conversion.auto-conversion-byline.legacyEditorTooltip',
		defaultMessage: 'This page is using the legacy editor.',
	},
});

export const AutoConversionByLine: FC = () => {
	const [isOpen, setIsOpen] = useState(false);
	const { featureFlagClient } = useSessionData();
	const [, autoConvertActions] = useAutoConvertState();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { editorProperty, fabricEditorSupported, queryDataId } = useAutoConversionByLineContext();

	const isAutoConverted = !editorProperty && fabricEditorSupported;

	// Update autoConverted state
	useEffect(() => {
		if (queryDataId && isAutoConverted) {
			autoConvertActions.setIsAutoConverted(isAutoConverted);
		}
	}, [queryDataId, isAutoConverted, autoConvertActions]);

	// Send analytics data
	useEffect(() => {
		if (queryDataId && isAutoConverted) {
			const settingData = {
				isAllPagesEnabled: featureFlagClient.getBooleanValue(ALL_PAGES_FF, {
					default: false,
				}),
			};

			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConvertEvent.VIEWED,
					source: 'ViewPageAutoConversion',
					setting: settingData,
				},
				AutoConvertActionSubject.AUTO_CONVERSION,
			);
		}
	}, [queryDataId, createAnalyticsEvent, featureFlagClient, isAutoConverted]);

	const showModal = useCallback(() => {
		sendEditorConversionAnalyticsPageEvent(
			createAnalyticsEvent,
			{
				pageAction: AutoConversionByLineEvent.CLICKED,
				source: 'AutoConversionByLine',
			},
			AutoConvertActionSubject.AUTO_CONVERSION_BYLINE,
		);

		if (isAutoConverted) {
			setIsOpen(true);
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConversionByLineEvent.NEW_EDITOR_MODAL_OPENED,
					source: 'AutoConversionByLine',
				},
				AutoConvertActionSubject.AUTO_CONVERSION_BYLINE,
			);
		}
	}, [setIsOpen, isAutoConverted, createAnalyticsEvent]);

	const hideModal = useCallback(() => {
		setIsOpen(false);
		if (isAutoConverted)
			sendEditorConversionAnalyticsPageEvent(
				createAnalyticsEvent,
				{
					pageAction: AutoConversionByLineEvent.NEW_EDITOR_MODAL_CLOSED,
					source: 'AutoConversionByLine',
				},
				AutoConvertActionSubject.AUTO_CONVERSION_BYLINE,
			);
	}, [setIsOpen, isAutoConverted, createAnalyticsEvent]);

	if (editorProperty && fabricEditorSupported) {
		return null;
	}

	return (
		<>
			<StyledByLine onClick={showModal} data-testid="auto-conversion-byline">
				<Tooltip
					tag="span"
					content={
						isAutoConverted ? (
							<FormattedMessage {...i18n.newEditorTooltip} />
						) : (
							<FormattedMessage {...i18n.legacyEditorTooltip} />
						)
					}
				>
					<span data-testid="auto-conversion-byline-text">
						<FormattedMessage
							{...i18n.byLine}
							values={{
								editorType: isAutoConverted ? 'New editor' : 'Legacy editor',
							}}
						/>
					</span>
				</Tooltip>
			</StyledByLine>
			<EditorConversionModalComponent isOpen={isOpen} onClose={hideModal} />
		</>
	);
};
